import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../../core/auth/auth.service';
import { GENERIC_ERROR } from '../../../core/http/read-error';

@Component({
  selector: 'app-reset-password',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  // Carried over from the login form via navigation state — never a URL query param.
  protected readonly form = this.fb.group({
    email: [
      (history.state as { email?: string })?.email ?? '',
      [Validators.required, Validators.email],
    ],
  });

  protected get emailControl() {
    return this.form.controls.email;
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.isLoading() || this.form.invalid) return;

    this.errorMessage.set(null);
    this.isLoading.set(true);

    this.authService.requestPasswordReset(this.emailControl.value!).subscribe({
      next: () => {
        this.isLoading.set(false);
        void this.router.navigate(['/auth/reset-password/email-sent'], {
          state: { email: this.emailControl.value! },
        });
      },
      error: (err: unknown) => {
        this.isLoading.set(false);
        if (err instanceof HttpErrorResponse) {
          const authErr = this.authService.getAuthError(err);
          this.errorMessage.set(authErr?.detail ?? GENERIC_ERROR);
        } else {
          this.errorMessage.set(GENERIC_ERROR);
        }
        this.cdr.markForCheck();
      },
    });
  }
}
